import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BadgeCheck, Check, ChevronDown, Coins, Crown, Eye, FileClock, GalleryHorizontal,
  Gamepad2, Gift, KeyRound, LayoutDashboard, Loader2, Lock, Plus, RefreshCw,
  Search, ShieldAlert, ShieldCheck, ShieldX, SlidersHorizontal, Ticket,
  TicketPercent, UserCog, UserPlus, Users, X,
} from 'lucide-react';
import client from '../lib/api';
import { apiErrorMessage } from '../lib/apiError';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { useToast } from '../hooks/use-toast';
import ConfirmDialog from './components/ConfirmDialog';
import { buildPermissionChecker } from './lib/permissions';
import { menuItemsByPermission, navigationAccess } from './lib/navigation';
import { formatDateTime } from './lib/formatters';
import './gameControl.css';
import './roles.css';

// Rolün rengi ve ikonu: kart, rozet ve avatar aynı tonu okur, böylece
// listede bir hesabın rolü metni okumadan da ayırt edilir.
const ROLE_TONES = {
  owner: '#fbbf24',
  finance: '#34d399',
  operations: '#38bdf8',
  bonus: '#c084fc',
  viewer: '#94a3b8',
  custom: '#22d3ee',
};

const ROLE_ICONS = {
  owner: Crown,
  finance: Coins,
  operations: SlidersHorizontal,
  bonus: Gift,
  viewer: Eye,
  custom: BadgeCheck,
};

// Yetki kataloğunun grupları backend'den metin olarak gelir; ikon eşlemesi
// tanınmayan grupta varsayılana düşer, yeni bir grup eklendiğinde ekran
// bozulmaz.
const GROUP_ICONS = {
  'Genel Bakış': LayoutDashboard,
  Kullanıcılar: Users,
  Finans: Coins,
  İçerik: GalleryHorizontal,
  Oyunlar: Gamepad2,
  Affiliate: UserPlus,
  Promosyon: TicketPercent,
  Bonus: Gift,
  Spor: Ticket,
  Denetim: FileClock,
  Yönetim: ShieldAlert,
};

// Yeni yetkili formu hazır bir paket dayatmaz: "Özel Yetki" ile açılır ve
// hiçbir izin işaretli gelmez, yetkiyi açan kişi kendi kurar.
const DEFAULT_ROLE = 'custom';

const emptyForm = {
  username: '',
  password: '',
  email: '',
  first_name: '',
  last_name: '',
  role: DEFAULT_ROLE,
  permissions: [],
};

// Backend sözleşmesinin aynısı (`models.AdminAccountCreate`): kural yalnızca
// sunucuda kalsaydı Türkçe karakterli kullanıcı adı ya da kısa şifre 422
// alıyor ve yönetici hangi alanın hatalı olduğunu göremiyordu.
const PASSWORD_MIN = 10;
const USERNAME_PATTERN = /^[A-Za-z0-9_.-]{3,64}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const accountFormProblem = (form) => {
  const username = form.username.trim();
  if (!username) return 'Kullanıcı adı zorunludur.';
  if (!USERNAME_PATTERN.test(username)) {
    return 'Kullanıcı adı 3-64 karakter olmalı; yalnızca İngilizce harf, rakam, '
      + 'nokta, tire ve alt çizgi içerebilir (boşluk ve ç, ğ, ı, ö, ş, ü olmaz).';
  }
  if (form.password.length < PASSWORD_MIN) {
    return `Şifre en az ${PASSWORD_MIN} karakter olmalıdır.`;
  }
  const email = form.email.trim();
  if (email && !EMAIL_PATTERN.test(email)) return 'E-posta adresi geçersiz.';
  return '';
};

const roleTone = (role) => ROLE_TONES[role] || ROLE_TONES.viewer;
const groupIcon = (group) => GROUP_ICONS[group] || ShieldCheck;

/** Bir iznin okuma eşi; yalnızca `*.write` anahtarlarında vardır. */
const readCounterpart = (key) => (
  key.endsWith('.write') ? key.replace(/\.write$/, '.read') : null
);

/** Bir iznin yazma eşi; yalnızca `*.read` anahtarlarında vardır. */
const writeCounterpart = (key) => (
  key.endsWith('.read') ? key.replace(/\.read$/, '.write') : null
);

/**
 * Verilen izin listesiyle açılan, yetkiye bağlı menü sayfası sayısı.
 *
 * İzinsiz maddeler (Kullanım Kılavuzu, Yenilikler, Hesap Güvenliği) sayıya
 * girmez: panele girebilen her yetkiliye açıktır ve yetki ölçüsünü şişirip
 * "hiç yetkisi yok" hesabı bile üç sayfalık gösterirdi.
 */
const guardedPages = (permissions, siteTheme) => navigationAccess(
  buildPermissionChecker(permissions), siteTheme,
).flatMap((group) => group.items).filter((item) => item.permission);

const pageAccessCount = (permissions, siteTheme) => (
  guardedPages(permissions, siteTheme).filter((item) => item.open).length
);

/* ------------------------------------------------------------ önizleme */

/**
 * Hesabın sol menüde göreceği ağaç.
 *
 * Kapalı maddeler de çizilir (üstü çizili): yöneticinin sorusu çoğu zaman
 * "neden bu sayfa yok" olur ve yalnızca açık olanları listeleyen bir
 * önizleme o soruyu ekranda cevapsız bırakır.
 */
const MenuPreview = ({ permissions, siteTheme }) => {
  const groups = useMemo(
    () => navigationAccess(buildPermissionChecker(permissions), siteTheme),
    [permissions, siteTheme],
  );
  return (
    <div className="rl-menu">
      {groups.map((group) => (
        <div
          key={group.label}
          className={`rl-menu-group ${group.openCount ? '' : 'is-empty'}`}
        >
          <h4>
            {group.label}
            <span>{group.openCount}/{group.items.length}</span>
          </h4>
          {group.items.map((item) => (
            <div
              key={item.key}
              className={`rl-menu-item ${item.open ? '' : 'is-closed'} ${
                item.permission ? '' : 'is-open-all'
              }`}
              title={item.permission
                ? `Gerekli yetki: ${[].concat(item.permission).join(' veya ')}`
                : 'Her yetkiliye açık'}
            >
              {item.open
                ? <Check size={12} aria-hidden="true" />
                : <Lock size={11} aria-hidden="true" />}
              <span>{item.label}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

/* ------------------------------------------------------- yetki seçicisi */

/**
 * Yetkiler menü grupları hâlinde kutulanır ve her satır, o iznin **menüde
 * hangi sayfaları açtığını** yazar.
 *
 * Anahtarın kendisi (`finance.read`) yöneticiye hiçbir şey anlatmıyordu:
 * eski ekran on dokuz onay kutusunu tek tek diziyor, hangi kutunun hangi
 * sayfayı açtığı yalnızca deneyerek öğreniliyordu. Sayfa listesi menü
 * haritasından türer (`menuItemsByPermission`), elle yazılmadığı için
 * menüye eklenen yeni sayfa burada kendiliğinden görünür.
 */
const PermissionMatrix = ({ catalog, role, roleMap, value, onChange }) => {
  const rolePermissions = useMemo(
    () => new Set(roleMap[role]?.permissions || []),
    [roleMap, role],
  );
  const isOwner = rolePermissions.has('*');
  const pageIndex = useMemo(() => menuItemsByPermission(), []);
  const catalogKeys = useMemo(
    () => new Set(catalog.map((item) => item.key)),
    [catalog],
  );
  const groups = useMemo(() => {
    const map = new Map();
    catalog.forEach((permission) => {
      if (!map.has(permission.group)) map.set(permission.group, []);
      map.get(permission.group).push(permission);
    });
    return Array.from(map.entries());
  }, [catalog]);

  if (isOwner) {
    return (
      <div className="gc-note is-warn">
        <Crown size={14} aria-hidden="true" />
        <span>
          Sistem Sahibi rolü panelin tamamını kapsar; yetkiler tek tek
          seçilemez ve ileride eklenen ekranlar da kendiliğinden açılır.
        </span>
      </div>
    );
  }

  /*
   * "Düzenleme" yetkisi tek başına hiçbir ekranı açmaz: panel sayfaları
   * okuma izniyle korunur. Bu yüzden yazma işaretlenince okuma da eklenir,
   * okuma kaldırılınca yazma düşer — aksi hâlde yönetici düzenleme yetkisi
   * verdiğini sanıp hesabı o sayfayı hiç göremez hâlde bırakıyordu.
   */
  const toggle = (key) => {
    if (rolePermissions.has(key)) return;
    if (value.includes(key)) {
      const paired = writeCounterpart(key);
      onChange(value.filter((item) => item !== key && item !== paired));
      return;
    }
    const next = [...value, key];
    const read = readCounterpart(key);
    if (read && catalogKeys.has(read) && !next.includes(read)) next.push(read);
    onChange(next);
  };

  return (
    <div className="rl-perms">
      {groups.map(([group, permissions]) => {
        const GroupIcon = groupIcon(group);
        const active = permissions.filter(
          (item) => rolePermissions.has(item.key) || value.includes(item.key),
        ).length;
        return (
          <section
            key={group}
            className={`rl-perm-group ${active ? 'is-on' : ''}`}
            style={{ '--gc-tone': active ? 'var(--gc-ok)' : 'var(--gc-muted)' }}
          >
            <header className="rl-perm-group-head">
              <GroupIcon size={14} aria-hidden="true" />
              <b>{group}</b>
              <span className={`gc-badge ${active ? 'is-on' : ''} is-count`}>
                {active}/{permissions.length}
              </span>
            </header>
            <div className="rl-perm-list">
              {permissions.map((permission) => {
                const fromRole = rolePermissions.has(permission.key);
                const checked = fromRole || value.includes(permission.key);
                const pages = pageIndex.get(permission.key) || [];
                return (
                  <label
                    key={permission.key}
                    className={`rl-perm ${checked ? 'is-on' : ''} ${
                      fromRole ? 'is-locked' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="rl-perm-input"
                      checked={checked}
                      disabled={fromRole}
                      onChange={() => toggle(permission.key)}
                      aria-label={permission.label}
                    />
                    <span className="gc-switch" aria-hidden="true">
                      <span className="gc-switch-track" />
                    </span>
                    <span className="rl-perm-text">
                      <span className="rl-perm-title">
                        {permission.label}
                        <span className="rl-perm-key">{permission.key}</span>
                        {fromRole && (
                          <span className="gc-badge is-info">Rolden geliyor</span>
                        )}
                      </span>
                      {pages.length ? (
                        <span className="rl-perm-pages">
                          {pages.map((page) => (
                            <span
                              key={`${permission.key}:${page.to}`}
                              className={`rl-page-chip ${checked ? 'is-on' : ''}`}
                              title={`${page.group} → ${page.label}`}
                            >
                              <page.icon size={11} aria-hidden="true" />
                              {page.label}
                            </span>
                          ))}
                        </span>
                      ) : (
                        <span className="rl-perm-pages">
                          <span className="rl-page-none">
                            Menüde ayrı sayfa açmaz; açık sayfalardaki
                            işlemleri yapmaya izin verir.
                          </span>
                        </span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
};

/* --------------------------------------------------------- rol seçicisi */

const RolePicker = ({ roles, selected, onSelect }) => (
  <div className="rl-role-pick">
    {roles.map((role) => {
      const Icon = ROLE_ICONS[role.key] || ShieldCheck;
      return (
        <button
          type="button"
          key={role.key}
          onClick={() => onSelect(role.key)}
          className={`rl-pick ${selected === role.key ? 'is-active' : ''}`}
          style={{ '--gc-tone': roleTone(role.key) }}
        >
          <span className="rl-pick-mark">
            {selected === role.key && <Check size={11} aria-hidden="true" />}
          </span>
          <span className="rl-pick-text">
            <b>
              <Icon size={12} aria-hidden="true" style={{ marginRight: 5, verticalAlign: '-1px' }} />
              {role.label}
            </b>
            <small>{role.description}</small>
          </span>
        </button>
      );
    })}
  </div>
);

/* ------------------------------------------------------------ hesap satırı */

const AccountRow = ({
  row, siteTheme, expanded, onToggle, onEdit, onPassword, onRevoke,
}) => {
  const Icon = ROLE_ICONS[row.role] || ShieldCheck;
  const allAccess = row.effective_permissions.includes('*');
  const pages = allAccess
    ? guardedPages(['*'], siteTheme).length
    : pageAccessCount(row.effective_permissions, siteTheme);
  return (
    <article
      className={`rl-account ${expanded ? 'is-open' : ''} ${
        row.is_self ? 'is-self' : ''
      } ${row.is_active ? '' : 'is-passive'}`}
      style={{ '--gc-tone': roleTone(row.role) }}
    >
      <div className="rl-account-row">
        <span className="rl-avatar" aria-hidden="true">
          {(row.username || '?').slice(0, 2)}
        </span>

        <div className="rl-who">
          <div className="rl-who-name">
            {row.username}
            {row.is_self && <span className="gc-badge is-info">Siz</span>}
            {!row.has_role_record && (
              <span className="gc-badge is-off">Rol kaydı yok</span>
            )}
          </div>
          <small title={row.email || undefined}>
            {row.full_name || row.email || 'Ad soyad girilmemiş'}
          </small>
        </div>

        <div className="rl-cell rl-cell-meta">
          <div className="rl-cell-label">Rol</div>
          <div className="rl-cell-value">
            <span
              className="gc-badge is-tone"
              style={{ '--gc-tone': roleTone(row.role) }}
            >
              <Icon size={11} aria-hidden="true" />
              {row.role_label}
            </span>
          </div>
        </div>

        <div className="rl-cell">
          <div className="rl-cell-label">Kapsam</div>
          <div className="rl-cell-value">
            {allAccess ? (
              <>Tüm yetkiler · <b>{pages}</b> sayfa</>
            ) : (
              <><b>{row.effective_permissions.length}</b> yetki · <b>{pages}</b> sayfa</>
            )}
          </div>
        </div>

        <div className="rl-cell rl-cell-state">
          <div className="rl-cell-label">Durum</div>
          <div className="rl-cell-value">
            <span className={`gc-badge ${row.is_active ? 'is-on' : 'is-off'}`}>
              {row.is_active ? 'Aktif' : 'Pasif'}
            </span>
          </div>
          <div className="rl-cell-value" style={{ fontSize: 11, color: 'var(--gc-dim)' }}>
            {row.last_login ? formatDateTime(row.last_login) : 'Hiç giriş yapmadı'}
          </div>
        </div>

        <div className="rl-account-actions">
          <button
            type="button"
            className={`gc-btn is-sm ${expanded ? 'is-tone' : ''}`}
            onClick={onToggle}
            aria-expanded={expanded}
          >
            <ChevronDown
              size={13}
              aria-hidden="true"
              style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 160ms ease' }}
            />
            Erişimi
          </button>
          <button
            type="button"
            className="gc-btn is-sm"
            disabled={row.is_self}
            title={row.is_self ? 'Kendi rolünüzü değiştiremezsiniz' : undefined}
            onClick={onEdit}
          >
            <UserCog size={13} aria-hidden="true" /> Rol & Yetki
          </button>
          <button type="button" className="gc-btn is-sm" onClick={onPassword}>
            <KeyRound size={13} aria-hidden="true" /> Şifre
          </button>
          <button
            type="button"
            className="gc-btn is-sm is-danger"
            disabled={row.is_self}
            title={row.is_self ? 'Kendi yetkinizi kaldıramazsınız' : undefined}
            onClick={onRevoke}
          >
            <ShieldX size={13} aria-hidden="true" /> Kaldır
          </button>
        </div>
      </div>

      {expanded && (
        <div className="rl-account-detail">
          <div className="gc-grouplabel">
            Panelde göreceği menü
            <span>
              {allAccess
                ? 'Sistem Sahibi: bütün menü açık, ileride eklenen ekranlar da dahil.'
                : 'Üstü çizili maddeler bu hesapta kapalıdır; adres çubuğundan da açılmaz.'}
            </span>
          </div>
          <MenuPreview
            permissions={row.effective_permissions}
            siteTheme={siteTheme}
          />
          {!allAccess && row.permissions.length > 0 && (
            <div className="gc-note is-info" style={{ marginTop: 10 }}>
              <Plus size={13} aria-hidden="true" />
              <span>
                Rolün üzerine tanımlanmış ek yetkiler:{' '}
                <b className="gc-mono">{row.permissions.join(', ')}</b>
              </span>
            </div>
          )}
        </div>
      )}
    </article>
  );
};

/* ----------------------------------------------------------------- sayfa */

const Roles = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  // Menü önizlemesi şemaya bağlı maddeleri (Merit'in yan paneli, Herkül'ün
  // sohbeti) yalnızca o şema yayındayken çizer; sidebar da aynı kuralı okur.
  const { site_theme: siteTheme } = useSettings();
  const [items, setItems] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissionCatalog, setPermissionCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null); // { type: 'create'|'edit'|'password', row }
  const [confirmation, setConfirmation] = useState(null);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [expanded, setExpanded] = useState('');

  const roleMap = useMemo(
    () => Object.fromEntries(roles.map((role) => [role.key, role])),
    [roles],
  );
  // Owner rolünü yalnızca owner dağıtabilir; diğerlerine seçenek gösterilmez.
  const isOwner = (user?.admin_permissions || []).includes('*');
  const assignableRoles = useMemo(
    () => roles.filter((role) => isOwner || role.key !== 'owner'),
    [roles, isOwner],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [list, catalog] = await Promise.all([
        client.get('/admin/roles'),
        client.get('/admin/roles/catalog'),
      ]);
      setItems(list.data.items || []);
      setRoles(catalog.data.roles || []);
      setPermissionCatalog(catalog.data.permissions || []);
    } catch (requestError) {
      setError(apiErrorMessage(requestError, 'Yetkili listesi alınamadı'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const revoke = async () => {
    if (!confirmation) return;
    setBusy(true);
    try {
      await client.delete(`/admin/roles/${confirmation.user_id}`);
      toast({ title: 'Yönetim yetkisi kaldırıldı' });
      setConfirmation(null);
      await load();
    } catch (requestError) {
      toast({
        title: 'İşlem başarısız',
        description: apiErrorMessage(requestError, 'Yetki kaldırılamadı'),
      });
    } finally {
      setBusy(false);
    }
  };

  const visible = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase('tr');
    return items.filter((row) => {
      if (roleFilter && row.role !== roleFilter) return false;
      if (!needle) return true;
      return [row.username, row.full_name, row.email, row.role_label]
        .filter(Boolean)
        .some((field) => field.toLocaleLowerCase('tr').includes(needle));
    });
  }, [items, query, roleFilter]);

  const summary = useMemo(() => ({
    total: items.length,
    active: items.filter((row) => row.is_active).length,
    owners: items.filter((row) => row.effective_permissions.includes('*')).length,
    orphans: items.filter((row) => !row.has_role_record).length,
  }), [items]);

  return (
    <div className="gc">
      <header className="gc-head">
        <span className="gc-head-icon"><UserCog size={22} aria-hidden="true" /></span>
        <div className="gc-head-text">
          <h1>Yetkililer & Roller</h1>
          <p>
            Panel erişimi olan hesaplar, rollerinin kapsamı ve her hesabın
            menüde hangi sayfaları görebildiği. Yetki değişikliği kaydedildiği
            anda hesabın açık oturumları sonlandırılır.
          </p>
        </div>
        <button type="button" onClick={load} className="gc-btn">
          <RefreshCw size={14} aria-hidden="true" /> Yenile
        </button>
        <button
          type="button"
          onClick={() => setModal({ type: 'create' })}
          className="gc-btn is-primary"
        >
          <UserPlus size={15} aria-hidden="true" /> Yeni Yetkili
        </button>
      </header>

      <div className="rl-summary">
        <div className="rl-sum" style={{ '--gc-tone': 'var(--gc-info)' }}>
          <span className="rl-sum-icon"><Users size={16} aria-hidden="true" /></span>
          <span className="rl-sum-text">
            <b>{summary.total}</b>
            <span>Yetkili hesap</span>
          </span>
        </div>
        <div className="rl-sum" style={{ '--gc-tone': 'var(--gc-ok)' }}>
          <span className="rl-sum-icon"><ShieldCheck size={16} aria-hidden="true" /></span>
          <span className="rl-sum-text">
            <b>{summary.active}</b>
            <span>Aktif</span>
          </span>
        </div>
        <div className="rl-sum" style={{ '--gc-tone': ROLE_TONES.owner }}>
          <span className="rl-sum-icon"><Crown size={16} aria-hidden="true" /></span>
          <span className="rl-sum-text">
            <b>{summary.owners}</b>
            <span>Tam yetkili</span>
          </span>
        </div>
        <div className={`rl-sum ${summary.orphans ? 'is-alert' : ''}`} style={{ '--gc-tone': 'var(--gc-muted)' }}>
          <span className="rl-sum-icon"><ShieldAlert size={16} aria-hidden="true" /></span>
          <span className="rl-sum-text">
            <b>{summary.orphans}</b>
            <span>Rol kaydı eksik</span>
          </span>
        </div>
      </div>

      <div className="gc-grouplabel">
        Roller
        <span>
          Rol, yetkilerin adlandırılmış paketidir; hesaba rolün üzerine ek
          yetki de tanımlanabilir. Karta basmak listeyi o role süzer.
        </span>
      </div>

      <div className="rl-roles">
        {roles.map((role) => {
          const Icon = ROLE_ICONS[role.key] || ShieldCheck;
          const count = items.filter((item) => item.role === role.key).length;
          const allAccess = role.permissions.includes('*');
          const pages = allAccess
            ? guardedPages(['*'], siteTheme).length
            : pageAccessCount(role.permissions, siteTheme);
          return (
            <button
              type="button"
              key={role.key}
              onClick={() => setRoleFilter(roleFilter === role.key ? '' : role.key)}
              className={`rl-role ${roleFilter === role.key ? 'is-active' : ''}`}
              style={{ '--gc-tone': roleTone(role.key) }}
              aria-pressed={roleFilter === role.key}
            >
              <div className="rl-role-head">
                <span className="rl-role-icon"><Icon size={15} aria-hidden="true" /></span>
                <b>{role.label}</b>
                <span className="rl-role-count">{count}</span>
              </div>
              <p>{role.description}</p>
              <div className="rl-role-facts">
                <span className="gc-badge is-count">
                  {allAccess ? 'Tüm yetkiler' : `${role.permissions.length} yetki`}
                </span>
                <span className="gc-badge is-count">{pages} sayfa</span>
              </div>
            </button>
          );
        })}
      </div>

      <div className="rl-toolbar">
        <div className="rl-search">
          <Search size={14} aria-hidden="true" />
          <input
            className="gc-input"
            placeholder="Kullanıcı adı, ad soyad veya e-posta ara"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            aria-label="Yetkili ara"
          />
        </div>
        {roleFilter && (
          <button
            type="button"
            className="gc-btn is-sm"
            onClick={() => setRoleFilter('')}
          >
            <X size={13} aria-hidden="true" />
            {roleMap[roleFilter]?.label || roleFilter} süzgecini kaldır
          </button>
        )}
        <span className="rl-toolbar-count">
          {visible.length} / {items.length} hesap
        </span>
      </div>

      {loading ? (
        <div className="gc-state">
          <Loader2 size={22} className="gc-spin" aria-hidden="true" />
          <h2>Yetkililer yükleniyor</h2>
        </div>
      ) : error ? (
        <div className="gc-state" role="alert">
          <ShieldAlert size={24} aria-hidden="true" />
          <h2>Liste alınamadı</h2>
          <p>{error}</p>
          <button type="button" onClick={load} className="gc-btn">
            <RefreshCw size={14} aria-hidden="true" /> Yeniden dene
          </button>
        </div>
      ) : visible.length === 0 ? (
        <div className="gc-state">
          <UserCog size={24} aria-hidden="true" />
          <h2>{items.length ? 'Süzgece uyan hesap yok' : 'Yetkili hesap yok'}</h2>
          <p>
            {items.length
              ? 'Arama ya da rol süzgecini temizleyerek tüm hesapları görebilirsiniz.'
              : 'Yeni Yetkili düğmesiyle finans, operasyon veya özel yetkili bir hesap açabilirsiniz.'}
          </p>
        </div>
      ) : (
        <div className="rl-accounts">
          {visible.map((row) => (
            <AccountRow
              key={row.user_id}
              row={row}
              siteTheme={siteTheme}
              expanded={expanded === row.user_id}
              onToggle={() => setExpanded(
                expanded === row.user_id ? '' : row.user_id,
              )}
              onEdit={() => setModal({ type: 'edit', row })}
              onPassword={() => setModal({ type: 'password', row })}
              onRevoke={() => setConfirmation(row)}
            />
          ))}
        </div>
      )}

      {modal && (
        <RoleModal
          modal={modal}
          roles={assignableRoles}
          roleMap={roleMap}
          permissionCatalog={permissionCatalog}
          currentUsername={user?.username}
          siteTheme={siteTheme}
          onClose={() => setModal(null)}
          onDone={async () => { setModal(null); await load(); }}
        />
      )}

      <ConfirmDialog
        open={Boolean(confirmation)}
        title="Yönetim yetkisini kaldır"
        description={confirmation
          ? `${confirmation.username} hesabının panel erişimi kaldırılacak ve açık oturumları sonlandırılacak. Hesabın kendisi silinmez.`
          : ''}
        confirmLabel="Yetkiyi kaldır"
        busy={busy}
        onCancel={() => !busy && setConfirmation(null)}
        onConfirm={revoke}
      />
    </div>
  );
};

/* ----------------------------------------------------------------- modal */

const RoleModal = ({
  modal, roles, roleMap, permissionCatalog, currentUsername, siteTheme,
  onClose, onDone,
}) => {
  const { type, row } = modal;
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(() => (
    type === 'edit'
      ? { ...emptyForm, role: row.role, permissions: row.permissions || [] }
      : emptyForm
  ));
  const [password, setPassword] = useState('');

  const selectedRole = roleMap[form.role];
  const rolePermissions = useMemo(
    () => selectedRole?.permissions || [],
    [selectedRole],
  );
  const allAccess = rolePermissions.includes('*');
  // Rolün getirdiği yetkilerle hesaba özel eklerin birleşimi: özet ve
  // önizleme backend'in `effective_permissions` hesabını aynalar.
  const effective = useMemo(
    () => (allAccess
      ? ['*']
      : Array.from(new Set([...rolePermissions, ...form.permissions]))),
    [allAccess, rolePermissions, form.permissions],
  );
  const pages = useMemo(
    () => (allAccess
      ? guardedPages(['*'], siteTheme).length
      : pageAccessCount(effective, siteTheme)),
    [allAccess, effective, siteTheme],
  );

  const submit = async () => {
    try {
      setBusy(true);
      if (type !== 'password' && form.role === 'custom' && !form.permissions.length) {
        toast({
          title: 'Yetki seçin',
          description: 'Özel yetki rolünde en az bir yetki işaretlenmelidir.',
        });
        return;
      }
      if (type === 'create') {
        const problem = accountFormProblem(form);
        if (problem) {
          toast({ title: 'Hesap bilgisi eksik', description: problem });
          return;
        }
        await client.post('/admin/roles', {
          username: form.username.trim(),
          password: form.password,
          email: form.email.trim() || null,
          first_name: form.first_name,
          last_name: form.last_name,
          role: form.role,
          permissions: form.permissions,
        });
        toast({
          title: 'Yetkili oluşturuldu',
          description: `${form.username} hesabı ${roleMap[form.role]?.label} rolüyle açıldı.`,
        });
      } else if (type === 'edit') {
        await client.patch(`/admin/roles/${row.user_id}`, {
          role: form.role,
          permissions: form.permissions,
        });
        toast({
          title: 'Rol güncellendi',
          description: `${row.username} hesabının oturumları sonlandırıldı.`,
        });
      } else {
        if (password.length < PASSWORD_MIN) {
          toast({
            title: 'Şifre kısa',
            description: `Şifre en az ${PASSWORD_MIN} karakter olmalıdır.`,
          });
          return;
        }
        await client.patch(`/admin/roles/${row.user_id}/password`, { password });
        toast({
          title: 'Şifre güncellendi',
          description: row.username === currentUsername
            ? 'Kendi şifrenizi değiştirdiniz, yeniden giriş yapmanız gerekecek.'
            : `${row.username} hesabının oturumları sonlandırıldı.`,
        });
      }
      await onDone();
    } catch (requestError) {
      // 422'de `detail` bir dizidir; düz metin değilse yönetici yalnızca
      // "Bir hata oluştu" görüyor ve hangi alanın hatalı olduğunu bilemiyordu.
      toast({
        title: 'İşlem başarısız',
        description: apiErrorMessage(requestError),
      });
    } finally {
      setBusy(false);
    }
  };

  const title = {
    create: 'Yeni Yetkili Oluştur',
    edit: `Rol & Yetki: ${row?.username || ''}`,
    password: `Şifre Sıfırla: ${row?.username || ''}`,
  }[type];

  const subtitle = {
    create: 'Hesap açılır ve seçilen rolün kapsamıyla panele girer.',
    edit: 'Kaydedildiğinde hesabın açık oturumları sonlandırılır.',
    password: 'Şifre değiştiğinde hesabın tüm açık oturumları kapatılır.',
  }[type];

  return (
    <div className="gc-modal" role="dialog" aria-modal="true" aria-label={title}>
      <button
        type="button"
        className="gc-modal-veil"
        aria-label="Kapat"
        onClick={onClose}
      />
      <div className={`gc-modal-card ${type === 'password' ? 'is-narrow' : ''}`}>
        <header className="gc-modal-head">
          <span
            className="gc-card-icon"
            style={{ '--gc-tone': type === 'password' ? 'var(--gc-warn)' : roleTone(form.role) }}
          >
            {type === 'password'
              ? <KeyRound size={17} aria-hidden="true" />
              : <UserCog size={17} aria-hidden="true" />}
          </span>
          <div className="gc-modal-head-text">
            <h3>{title}</h3>
            <p>{subtitle}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="gc-btn is-icon is-sm"
            aria-label="Kapat"
          >
            <X size={14} aria-hidden="true" />
          </button>
        </header>

        {type === 'password' ? (
          <div className="gc-modal-single">
            <label className="gc-field" htmlFor="admin-new-password">
              <span className="gc-label">
                Yeni şifre
                <span className="gc-label-tag">en az 10 karakter</span>
              </span>
              <input
                id="admin-new-password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="gc-input"
              />
            </label>
            <div className="gc-note is-warn">
              <ShieldAlert size={13} aria-hidden="true" />
              <span>
                Şifre kaydedildiği anda bu hesabın bütün oturumları kapanır;
                kendi hesabınızsa yeniden giriş yapmanız gerekir.
              </span>
            </div>
          </div>
        ) : (
          <div className="gc-modal-body">
            <aside className="gc-modal-side">
              {type === 'create' && (
                <div className="gc-stack">
                  <div className="gc-grouplabel" style={{ margin: 0 }}>Hesap</div>
                  <label className="gc-field" htmlFor="admin-username">
                    <span className="gc-label">
                      Kullanıcı Adı
                      <span className="gc-label-tag">harf, rakam, _ . -</span>
                    </span>
                    <input
                      id="admin-username"
                      value={form.username}
                      onChange={(event) => setForm({ ...form, username: event.target.value })}
                      className="gc-input"
                    />
                  </label>
                  <label className="gc-field" htmlFor="admin-password">
                    <span className="gc-label">
                      Şifre
                      <span className="gc-label-tag">en az 10 karakter</span>
                    </span>
                    <input
                      id="admin-password"
                      type="password"
                      autoComplete="new-password"
                      value={form.password}
                      onChange={(event) => setForm({ ...form, password: event.target.value })}
                      className="gc-input"
                    />
                  </label>
                  <div className="gc-fields">
                    <label className="gc-field" htmlFor="admin-first-name">
                      <span className="gc-label">Ad</span>
                      <input
                        id="admin-first-name"
                        value={form.first_name}
                        onChange={(event) => setForm({ ...form, first_name: event.target.value })}
                        className="gc-input"
                      />
                    </label>
                    <label className="gc-field" htmlFor="admin-last-name">
                      <span className="gc-label">Soyad</span>
                      <input
                        id="admin-last-name"
                        value={form.last_name}
                        onChange={(event) => setForm({ ...form, last_name: event.target.value })}
                        className="gc-input"
                      />
                    </label>
                  </div>
                  <label className="gc-field" htmlFor="admin-email">
                    <span className="gc-label">E-Posta</span>
                    <input
                      id="admin-email"
                      type="email"
                      value={form.email}
                      onChange={(event) => setForm({ ...form, email: event.target.value })}
                      className="gc-input"
                    />
                  </label>
                </div>
              )}

              <div>
                <div className="gc-grouplabel" style={{ marginTop: 0 }}>
                  Rol
                  <span>Hazır paket; seçim sağdaki yetkileri birden değiştirir.</span>
                </div>
                <RolePicker
                  roles={roles}
                  selected={form.role}
                  onSelect={(role) => setForm({ ...form, role, permissions: [] })}
                />
              </div>

              <div>
                <div className="gc-grouplabel" style={{ marginTop: 0 }}>Bu hesabın kapsamı</div>
                <div className="rl-scope">
                  <div className={`rl-scope-box ${effective.length ? '' : 'is-zero'}`}>
                    <b>{allAccess ? '∞' : effective.length}</b>
                    <span>Yetki</span>
                  </div>
                  <div className={`rl-scope-box ${pages ? '' : 'is-zero'}`}>
                    <b>{pages}</b>
                    <span>Menü sayfası</span>
                  </div>
                </div>
              </div>
            </aside>

            <div className="gc-modal-main">
              <div>
                <div className="gc-grouplabel" style={{ marginTop: 0 }}>
                  Yetkiler
                  <span>
                    {form.role === 'custom'
                      ? 'Hazır paket yok: hesabın erişimi buradan tek tek kurulur. Her satır, o yetkinin menüde açtığı sayfaları yazar.'
                      : 'Rolün kapsadığı yetkiler kilitlidir; üzerine ek yetki verebilirsiniz. Her satır, o yetkinin menüde açtığı sayfaları yazar.'}
                  </span>
                </div>
                {form.role === 'custom' && !form.permissions.length && (
                  <div className="gc-note is-warn" style={{ marginBottom: 10 }}>
                    <ShieldAlert size={13} aria-hidden="true" />
                    <span>
                      Henüz yetki seçilmedi. Bu hâliyle hesap panelde hiçbir
                      sayfayı göremez; en az bir yetki işaretleyin.
                    </span>
                  </div>
                )}
                <PermissionMatrix
                  catalog={permissionCatalog}
                  role={form.role}
                  roleMap={roleMap}
                  value={form.permissions}
                  onChange={(permissions) => setForm({ ...form, permissions })}
                />
              </div>

              <div>
                <div className="gc-grouplabel">
                  Menü önizlemesi
                  <span>
                    Bu hesap panele girdiğinde sol menüyü böyle görecek; üstü
                    çizili maddeler kapalıdır.
                  </span>
                </div>
                <MenuPreview permissions={effective} siteTheme={siteTheme} />
              </div>
            </div>
          </div>
        )}

        <footer className="gc-modal-foot">
          <span className="gc-foot-note">
            {type === 'password'
              ? 'Şifreyi kaydettiğinizde hesap yeniden giriş yapmak zorunda kalır.'
              : `Seçilen rol: ${selectedRole?.label || '—'}`}
          </span>
          <button type="button" className="gc-btn" onClick={onClose}>
            Vazgeç
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={submit}
            className="gc-btn is-primary"
          >
            {busy && <Loader2 size={14} className="gc-spin" aria-hidden="true" />}
            {type === 'create' && 'Yetkiliyi Oluştur'}
            {type === 'edit' && 'Rolü Kaydet'}
            {type === 'password' && 'Şifreyi Güncelle'}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default Roles;
